/**
 * Pure presentation helpers for the `/pr` page — labels for both lifecycles
 * (engagement STAGE and contact STATUS), the people-card filter, and the
 * email-engagement badge mapping. Kept separate from the page so the logic is
 * unit-testable (the server groups + orders; this only narrows + labels — no
 * data fetching).
 *
 * Supersedes `outreach-board.ts`. Carried forward with three changes: the type
 * filter learned `local_partner` (migration 126), the vocabulary gained the
 * engagement stage machine, and `matchesPerson` searches the angle too.
 */

import type {
  PrBoardPerson,
  PrEngagementStage,
  PrExchangeKind,
} from "./api";

// ---------------------------------------------------------------------------
// Contact type — journalist / influencer / local partner.
// ---------------------------------------------------------------------------

export type PrTypeFilter = "all" | "journalist" | "influencer" | "local_partner";

export const PR_TYPE_TABS: ReadonlyArray<{
  readonly value: PrTypeFilter;
  readonly label: string;
}> = [
  { value: "all", label: "All" },
  { value: "journalist", label: "Journalists" },
  { value: "influencer", label: "Influencers" },
  { value: "local_partner", label: "Local partners" },
];

const TYPE_BADGES: Record<string, string> = {
  journalist: "✍️ Journalist",
  influencer: "📸 Influencer",
  local_partner: "📍 Local partner",
};

/** Chip label for a contact type (falls back to the raw value). */
export function typeBadge(type: string): string {
  return TYPE_BADGES[type] ?? type;
}

// ---------------------------------------------------------------------------
// Engagement stage — the COLLABORATION lifecycle.
// ---------------------------------------------------------------------------

const STAGE_LABELS: Record<PrEngagementStage, string> = {
  pitching: "Drafting",
  pitched: "Pitched",
  replied: "Replied",
  agreed: "Agreed",
  prepped: "Prepped",
  session_done: "Session done",
  delivered: "Delivered",
  closed: "Closed",
};

/** Human label for a pipeline stage (falls back to Capitalised raw value). */
export function stageLabel(stage: string): string {
  const known = STAGE_LABELS[stage as PrEngagementStage];
  if (known !== undefined) return known;
  const first = stage.charAt(0);
  return first === "" ? stage : first.toUpperCase() + stage.slice(1);
}

const EXCHANGE_LABELS: Record<PrExchangeKind, string> = {
  publication: "Publication",
  social_content: "Social content",
  paying_plus_one: "Paying +1",
  partnership: "Partnership",
};

/** What we asked for in return for the session. */
export function exchangeLabel(kind: string): string {
  const known = EXCHANGE_LABELS[kind as PrExchangeKind];
  if (known !== undefined) return known;
  return kind.replace(/_/g, " ");
}

// ---------------------------------------------------------------------------
// Contact status — the PERSON lifecycle (`outreach_target_status`).
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  discovered: "Discovered",
  qualified: "Qualified",
  queued: "Queued",
  contacted: "Contacted",
  responded: "Responded",
  dormant: "Dormant",
  dropped: "Dropped",
};

/** Human label for a contact status (falls back to Capitalised raw value). */
export function statusLabel(status: string): string {
  const known = STATUS_LABELS[status];
  if (known !== undefined) return known;
  const first = status.charAt(0);
  return first === "" ? status : first.toUpperCase() + status.slice(1);
}

// ---------------------------------------------------------------------------
// People filter.
// ---------------------------------------------------------------------------

export interface PrPeopleFilters {
  readonly type: PrTypeFilter;
  readonly query: string;
  /** Minimum quality score; null ⇒ no floor. A contact with a null score is
   *  excluded once a floor is set (it hasn't cleared the bar). */
  readonly minScore: number | null;
}

export const EMPTY_PR_FILTERS: PrPeopleFilters = {
  type: "all",
  query: "",
  minScore: null,
};

export function matchesPerson(p: PrBoardPerson, f: PrPeopleFilters): boolean {
  if (f.type !== "all" && p.type !== f.type) return false;
  if (f.minScore !== null) {
    if (p.quality_score === null || p.quality_score < f.minScore) return false;
  }
  const q = f.query.trim().toLowerCase();
  if (q.length > 0) {
    const hay = [
      p.name,
      p.company_outlet,
      p.niche,
      p.location,
      p.next_action,
      p.suggested_angle,
    ]
      .filter((s): s is string => s !== null && s.length > 0)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

/** Narrow the people list by the active filters, preserving server order. */
export function filterPeople(
  people: ReadonlyArray<PrBoardPerson>,
  f: PrPeopleFilters,
): ReadonlyArray<PrBoardPerson> {
  return people.filter((p) => matchesPerson(p, f));
}

/** Whether any filter is actually narrowing (drives the "showing X" hint). */
export function filtersActive(f: PrPeopleFilters): boolean {
  return f.type !== "all" || f.query.trim().length > 0 || f.minScore !== null;
}

// ---------------------------------------------------------------------------
// Email engagement badge.
// ---------------------------------------------------------------------------

export interface EmailEventDisplay {
  readonly label: string;
  readonly tone: "good" | "bad" | "neutral";
}

/** Map a Resend event type to a badge label + tone. A bounce/complaint is the
 *  send-and-learn signal that the email is unreliable. */
export function emailEventDisplay(type: string): EmailEventDisplay {
  switch (type) {
    case "clicked":
      return { label: "🔗 clicked", tone: "good" };
    case "opened":
      return { label: "👀 opened", tone: "good" };
    case "delivered":
      return { label: "📬 delivered", tone: "neutral" };
    case "bounced":
      return { label: "⚠️ bounced", tone: "bad" };
    case "complained":
      return { label: "🚫 complained", tone: "bad" };
    default:
      return { label: type, tone: "neutral" };
  }
}

// ---------------------------------------------------------------------------
// The per-stage timestamp strip — Mario's "extremely organized, all timestamped".
// ---------------------------------------------------------------------------

/** One step of an engagement's progress strip. */
export interface PrProgressStep {
  readonly stage: PrEngagementStage;
  readonly label: string;
  readonly at: string | null;
}

/**
 * The stamps a row displays, in pipeline order. `pitching` is omitted — it is
 * the initial state and has no stamp of its own (`created_at` records it), and
 * `closed` is omitted because it is an outcome rather than a step: a closed
 * engagement shows its `closed_reason` instead of an empty pip.
 *
 * 2026-08-15 (inbox-pr-simplify) — THREE pips, matching the simplified funnel
 * (pitched → agreed → delivered). Replies, prep and sessions became timeline
 * EVENTS; their legacy stamp columns survive on old rows but no longer earn a
 * pip — the timeline on the person page is the record.
 */
export function progressSteps(e: {
  readonly pitched_at: string | null;
  readonly agreed_at: string | null;
  readonly delivered_at: string | null;
}): ReadonlyArray<PrProgressStep> {
  return [
    { stage: "pitched", label: "Pitched", at: e.pitched_at },
    { stage: "agreed", label: "Agreed", at: e.agreed_at },
    { stage: "delivered", label: "Delivered", at: e.delivered_at },
  ];
}
